'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { IoPersonCircleOutline } from 'react-icons/io5';
import styles from '../styles/Menu.module.css';
import { useAuth } from '../../context/AuthContext';
import SignInModal from './SignInModal';

const Menu = ({ isOpen }) => {
  const menuRef = useRef(null);
  const menuItemsRef = useRef([]);
  const desktopMenuRef = useRef(null);
  const desktopMenuItemsRef = useRef([]);
  const { isAuthenticated } = useAuth();
  const [showSignInModal, setShowSignInModal] = useState(false);

  useEffect(() => {
    const menu = menuRef.current;
    const menuItems = menuItemsRef.current;
    
    if (!menu || menuItems.length === 0) return;
    
    // Initial setup - match the original menu.js
    gsap.set(menu, { y: 50, opacity: 0, pointerEvents: 'none' });
    gsap.set(menuItems, { y: 50, opacity: 0, pointerEvents: 'none' });
    
    if (isOpen) {
      // Open menu animation
      gsap.to(menu, {
        y: 0,
        opacity: 1,
        pointerEvents: 'auto',
        duration: 0.5,
        ease: 'power2.out',
      });
      
      gsap.to(menuItems, {
        pointerEvents: 'all',
        y: 0,
        opacity: 1,
        duration: 0.5,
        stagger: 0.05,
        ease: 'power2.out',
      });
    } else {
      // Close menu animation
      gsap.to(menu, {
        y: -50,
        opacity: 0,
        pointerEvents: 'none',
        duration: 0.5,
        ease: 'power2.out',
        onComplete: () => {
          gsap.set(menu, { y: 50, opacity: 0, pointerEvents: 'none' });
          gsap.set(menuItems, { y: 50, opacity: 0, pointerEvents: 'none' });
        },
      });
    }
  }, [isOpen]);

  // Desktop menu animation
  useEffect(() => {
    const desktopMenu = desktopMenuRef.current;
    const desktopItems = desktopMenuItemsRef.current;
    
    if (!desktopMenu || desktopItems.length === 0) return;
    
    // Initial setup for desktop menu
    gsap.set(desktopItems, { 
      y: -20, 
      opacity: 0,
      scale: 0.9
    });
    
    // Animate in desktop menu items
    gsap.to(desktopItems, {
      y: 0,
      opacity: 1,
      scale: 1,
      duration: 0.5,
      stagger: 0.05,
      ease: 'power2.out',
      delay: 0.2
    });
  }, []);

  // Add items to refs array
  const addToMenuItemsRef = (el) => {
    if (el && !menuItemsRef.current.includes(el)) {
      menuItemsRef.current.push(el);
    }
  };

  // Add desktop items to refs array
  const addToDesktopMenuItemsRef = (el) => {
    if (el && !desktopMenuItemsRef.current.includes(el)) {
      desktopMenuItemsRef.current.push(el);
    }
  };

  // Menu items data
  const menuItems = [
    { id: '01', label: 'How to buy', href: '/#wallet-connect' },
    { id: '02', label: 'Tokenomics', href: '/#tokenomics' },
    { id: '03', label: 'Roadmap', href: '/#roadmap' },
    { id: '04', label: 'FAQ', href: '/#faq' },
    { id: '05', label: 'Whitepaper', href: 'https://wp.gamtoken.com/pz-whitepaper', external: true },
    { id: '06', label: 'Profile', href: '/profile', isProfile: true }, // Always include profile
  ];
  
  const allMenuItems = menuItems;

  // Handler for profile click
  const handleProfileClick = (e) => {
    if (!isAuthenticated) {
      e.preventDefault();
      setShowSignInModal(true);
    }
    // else, let Link navigate to /profile
  };

  return (
    <>
      {/* Desktop horizontal menu */}
      <div className={styles.desktopMenu} ref={desktopMenuRef}>
        {allMenuItems.map((item) => (
          item.external ? (
            <a 
              key={item.id}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.desktopMenuItem}
              ref={addToDesktopMenuItemsRef}
            >
              {item.label}
            </a>
          ) : (
            item.isProfile ? (
            <Link 
              key={item.id}
              href={item.href}
                className={`${styles.desktopMenuItem} ${styles.profileButton}`}
              ref={addToDesktopMenuItemsRef}
                onClick={handleProfileClick}
            >
                <span className={styles.profileIcon}>
                  <IoPersonCircleOutline size={20} />
                </span>
              </Link>
              ) : (
              <Link 
                key={item.id}
                href={item.href}
                className={styles.desktopMenuItem}
                ref={addToDesktopMenuItemsRef}
              >
                {item.label}
            </Link>
            )
          )
        ))}
      </div>

      {/* Mobile dropdown menu */}
      <div className={styles.menuContainer} ref={menuRef}>
        <div className={styles.whitespace}></div>
        <div className={styles.whitespace}></div>
        <div className={styles.menuItems}>
          {allMenuItems.map((item) => (
            item.external ? (
              <a 
                key={item.id}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.menuItem}
                ref={addToMenuItemsRef}
              >
                <span>{item.id}</span>
                <span>{item.label}</span>
              </a>
            ) : (
              item.isProfile ? (
              <Link 
                key={item.id}
                href={item.href}
                  className={`${styles.menuItem} ${styles.profileMenuButton}`}
                ref={addToMenuItemsRef}
                  onClick={handleProfileClick}
              >
                <span>{item.id}</span>
                <span className={styles.menuItemLabel}>
                      <IoPersonCircleOutline size={18} className={styles.mobileProfileIcon} />
                      {item.label}
                </span>
              </Link>
              ) : (
                <Link 
                  key={item.id}
                  href={item.href}
                  className={styles.menuItem}
                  ref={addToMenuItemsRef}
                >
                  <span>{item.id}</span>
                  <span className={styles.menuItemLabel}>{item.label}</span>
                </Link>
              )
            )
          ))}
        </div>
      </div>

      {/* Sign-in Modal */}
      <SignInModal open={showSignInModal} onClose={() => setShowSignInModal(false)} />
    </>
  );
};

export default Menu; 
